import React, { useCallback } from 'react';
import { TypedMemo } from '../../common/types';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { TrackPlaylistModal } from '../components/TrackPlaylistModal/TrackPlaylistModal';
import { BrowseRowProvider } from './BrowseContext';
import { useBrowsePage } from './hooks/useBrowsePage';
import { BrowseSearchPanel } from './search/BrowseSearchPanel';
import { BrowseResults } from './results/BrowseResults';
import { BrowseFirstVisit } from './states/BrowseFirstVisit';
import { BrowseCreditsPanel } from './rail/BrowseCreditsPanel';
import { BrowseFollowSlots } from './rail/BrowseFollowSlots';
import { BrowseRecentSearches } from './rail/BrowseRecentSearches';
import { BrowseFilterSheet } from './phone/BrowseFilterSheet';
import { BrowseCreditsSheet } from './phone/BrowseCreditsSheet';
import styles from './Browse.module.scss';

interface BrowsePageProps {
  /** The page state. `SMLBrowseHome` owns it, so the credits pill in the page head shares it. */
  page: ReturnType<typeof useBrowsePage>;
}

/**
 * Browse v2 (design `docs/superpowers/design-reference/music24-browse-v2/`, frames a, b, c): the
 * search panel and the results on the left, the credits, the follow slots and the recent searches
 * in the rail. A phone has one column and two sheets.
 */
const BrowsePageComponent: React.FC<BrowsePageProps> = ({ page }) => {
  const router = useSMLRouter();
  const { state, update, search, usage, recent } = page;
  const { setPasteOpen, setFilterSheetOpen, setCreditsSheetOpen } = page;
  const openPaste = useCallback(() => setPasteOpen(true), [setPasteOpen]);
  const onTracked = useCallback(
    (playlistId: string) => {
      setPasteOpen(false);
      router.push(SMLRoutes.playlistDetails, { playlistId });
    },
    [router, setPasteOpen]
  );

  return (
    <BrowseRowProvider usage={usage} onBlockedOpen={page.onBlockedOpen} onOpenRow={page.onOpenRow}>
      <div className={styles.layout}>
        <div className={styles.main}>
          <BrowseSearchPanel
            state={state}
            update={update}
            data={page.hasQuery ? search.data : undefined}
            hasQuery={page.hasQuery}
            onSubmit={page.onSubmit}
            onPasteLink={openPaste}
            onOpenFilterSheet={() => setFilterSheetOpen(true)}
          />
          {page.hasQuery ? (
            <BrowseResults
              search={search}
              query={state.query.trim()}
              type={state.type}
              filterOn={page.filterOn}
              usage={usage}
              onTypeChange={page.onTypeChange}
              onShowMore={page.onShowMore}
              onSearch={page.onSearch}
              onClearFilters={page.onClearFilters}
              onPasteLink={openPaste}
            />
          ) : (
            <BrowseFirstVisit
              onSearch={page.onSearch}
              onPasteLink={openPaste}
              creditsLeft={usage?.left ?? null}
              creditsLimit={usage?.limit ?? null}
            />
          )}
        </div>
        <aside className={styles.rail}>
          {usage !== undefined && <BrowseCreditsPanel usage={usage} />}
          {usage !== undefined && <BrowseFollowSlots usage={usage} />}
          <BrowseRecentSearches items={recent.items} onPick={page.onSearch} onClear={recent.clear} />
        </aside>
      </div>
      <TrackPlaylistModal isOpen={page.pasteOpen} onClose={() => setPasteOpen(false)} onSuccess={onTracked} />
      {page.filterSheetOpen && (
        <BrowseFilterSheet
          state={state}
          update={update}
          resultCount={page.typeCount}
          onClose={() => setFilterSheetOpen(false)}
        />
      )}
      {page.creditsSheetOpen && usage !== undefined && (
        <BrowseCreditsSheet usage={usage} onClose={() => setCreditsSheetOpen(false)} />
      )}
    </BrowseRowProvider>
  );
};

export const BrowsePage = TypedMemo(BrowsePageComponent);
